import React from "react"
import styled, { keyframes } from "styled-components"

import { FaChevronRight } from "react-icons/fa"
import { ReactComponent as Elo } from 'assets/images/icons/elo.svg';
import { ReactComponent as Visa } from 'assets/images/icons/visa.svg';
import { ReactComponent as Mastercard } from 'assets/images/icons/master.svg';
import { ReactComponent as Pix } from 'assets/images/icons/pix-fit.svg';

const CardContainer = styled.div<{ disabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  transition: background 0.2s ease-in-out;
  width: 100%;

   ${({ disabled }) =>
    disabled &&
    `
    background: #f9fafb;
    cursor: not-allowed;
    opacity: 0.7;
    pointer-events: none;
  `}

  &:hover {
    background: #f3f4f6;
    cursor: pointer;
    box-shadow: none;
    }

    @media (max-width: 768px) {
    text-align: center;
    flex-direction: column;
    gap: 12px;
  }
`

const ContentSection = styled.div`
  flex: 1;
`

const Title = styled.h3`
  font-size: 18px;
  font-weight: 500;
  color: #111827;
  margin: 0 0 4px 0;
`

const Subtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`

const PaymentSection = styled.div`
  display: flex;
  align-items: center;
  gap: 22px;
  margin-left: 16px;
`

const MastercardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`

const VisaIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const EloIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
`

const ArrowIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #9ca3af;
`

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`

const LoadingCardContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  width: 100%;
  cursor: default;
`

const LoadingContentSection = styled.div`
  flex: 1;
`

const SkeletonBase = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: 4px;
`

const LoadingTitle = styled(SkeletonBase)`
  height: 22px;
  width: 180px;
  margin: 0 0 8px 0;
`

const LoadingSubtitle = styled(SkeletonBase)`
  height: 16px;
  width: 120px;
  margin: 0;
`

// Seção de pagamento loading
const LoadingPaymentSection = styled.div`
  display: flex;
  align-items: center;
  gap: 22px;
  margin-left: 16px;
`

// Skeleton para ícones de pagamento
const LoadingPaymentIcon = styled(SkeletonBase)`
  width: 32px;
  height: 20px;
  border-radius: 2px;
`

// Skeleton para o ícone Elo (menor)
const LoadingEloIcon = styled(SkeletonBase)`
  width: 26px;
  height: 20px;
  border-radius: 2px;
`

// Skeleton para a seta
const LoadingArrowIcon = styled(SkeletonBase)`
  width: 16px;
  height: 16px;
  border-radius: 2px;
`

// Componente principal do card loading
export const LoadingCard = () => {
  return (
    <LoadingCardContainer>
      <LoadingContentSection>
        <LoadingTitle />
        <LoadingSubtitle />
      </LoadingContentSection>
      <LoadingPaymentSection>
        <LoadingPaymentIcon />
        <LoadingPaymentIcon />
        <LoadingEloIcon />
        <LoadingArrowIcon />
      </LoadingPaymentSection>
    </LoadingCardContainer>
  )
}
interface PaymentIconProps {
  type: "mastercard" | "visa" | "elo" | "pix";
}

const PaymentIcon = ({ type }: PaymentIconProps) => {
  switch (type) {
    case "mastercard":
      return (
        <MastercardIcon>
          <Mastercard height={26} />
        </MastercardIcon>
      )
    case "visa":
      return <VisaIcon><Visa height={26} /></VisaIcon>
    case "elo":
      return <EloIcon><Elo height={26} /></EloIcon>
    case "pix":
      return <EloIcon><Pix height={20} /></EloIcon>
    default:
      return null
  }
}

interface CardMehotdPaymentProps {
  title: string
  description: string
  isCard?: boolean
  isPix?: boolean
  onClick?: () => void
  isDisabled?: boolean
  isLoading: boolean
  "data-testid"?: string
}

export default function CardMethodPayment({ title, description, onClick, isCard = false, isPix = false, isDisabled = false, isLoading = false, "data-testid": dataTestId }: CardMehotdPaymentProps) {
  if (isLoading) {
    return <LoadingCard />
  }

  return (
    <CardContainer data-testid={dataTestId} onClick={onClick} disabled={isDisabled}>
      <ContentSection>
        <Title>{title}</Title>
        <Subtitle>{description}</Subtitle>
      </ContentSection>

      <PaymentSection>
        {isCard &&
          <>
            <PaymentIcon type="mastercard" />
            <PaymentIcon type="visa" />
            <PaymentIcon type="elo" />
          </>
        }
        {isPix &&
          <PaymentIcon type="pix" />
        }
        <ArrowIcon><FaChevronRight height={26} /></ArrowIcon>
      </PaymentSection>
    </CardContainer>
  )
}